// Authentication Service for Frontend
class AuthService {
  constructor() {
    // Must follow VITE_API_URL like the other services, or admin auth talks to
    // a different host than every other request.
    this.baseURL = import.meta.env.VITE_API_URL || 'https://api.kgcinnovations.com/api/v2';
    this.tokenKey = 'auth_token';
    this.userKey = 'auth_user';
  }

  // Login
  async login(email, password) {
    try {
      const response = await fetch(`${this.baseURL}/auth/sign-in`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Login failed');
      }

      if(data.data.user.role !== 'admin') {
        throw new Error('Unauthorized: Admin access required');
      }

      // Store token and user data
      this.setToken(data.data.token);
      this.setUser(data.data.user);

      return data.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  // Logout
  async logout() {
    try {
      // Call logout endpoint if we have a token
      const token = this.getToken();
      if (token) {
        await fetch(`${this.baseURL}/auth/sign-out`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Continue with local logout even if server call fails
    } finally {
      // Always clear local storage
      this.clearAuth();
    }
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = this.getToken();
    const user = this.getUser();
    return !!(token && user);
  }

  // Get current user
  getCurrentUser() {
    return this.getUser();
  }

  // Get auth token for API requests
  getAuthToken() {
    return this.getToken();
  }

  // Set authentication token
  setToken(token) {
    localStorage.setItem(this.tokenKey, token);
  }

  // Get authentication token
  getToken() {
    return localStorage.getItem(this.tokenKey);
  }

  // Set user data
  setUser(user) {
    localStorage.setItem(this.userKey, JSON.stringify(user));
  }

  // Get user data
  getUser() {
    const userData = localStorage.getItem(this.userKey);
    return userData ? JSON.parse(userData) : null;
  }

  // Clear all auth data
  clearAuth() {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userKey);
  }

  // Get auth headers for API requests
  getAuthHeaders() {
    const token = this.getToken();
    // console.log('Getting auth headers, token exists:', !!token);
    if (token) {
      // console.log('Token preview:', token.substring(0, 10) + '...');
      // Use X-Auth-Token instead of Authorization header to avoid server stripping
      return { 'Authorization': `Bearer ${token}` };
    }
    return {};
  }

  // Verify token with server
  async verifyToken() {
    try {
      const token = this.getToken();
      if (!token) {
        return false;
      }

      const response = await fetch(`${this.baseURL}/auth/verify`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        this.clearAuth();
        return false;
      }

      const data = await response.json();
      // Update user data if server returned updated info
      if (data.success && data.data && data.data.user) {
        this.setUser(data.data.user);
        return true;
      }

      this.clearAuth();
      return false;
    } catch (error) {
      console.error('Token verification error:', error);
      this.clearAuth();
      return false;
    }
  }
}

export default new AuthService();